import { Check, Loader, Flag, AlertTriangle, Briefcase, Gauge, FileSpreadsheet, Wallet, Users } from "lucide-react";
import { Card, Eyebrow, Reveal, Section, SectionHeader, StatusBadge } from "../deck/primitives";

const PHASES = [
  { key: "completed", title: "Completed", icon: Check, s: "verified", items: [
    "Arqitek originates project concept",
    "Proposal and Letter of Intent submitted to City",
    "Initial discussions with Scott Agajanian, Deputy City Manager, City of Murrieta",
    "City expresses interest in further review",
  ]},
  { key: "progress", title: "Currently In Progress", icon: Loader, s: "preliminary", items: [
    "City Attorney reviewing Surplus Land Act implications",
    "Scott Agajanian, Deputy City Manager, City of Murrieta, preparing potential Closed Session discussion with City Council",
    "City requests Arqitek ability-to-perform information",
  ]},
  { key: "target", title: "Target Next Milestone", icon: Flag, s: "target", items: [
    "Exclusive Negotiating Agreement or equivalent protected predevelopment agreement",
    "Permanent development / ground lease structure",
  ]},
];

const ASKS = [
  { icon: Briefcase, t: "Development experience" },
  { icon: Gauge, t: "Ability to perform" },
  { icon: FileSpreadsheet, t: "Preliminary pro forma" },
  { icon: Wallet, t: "Funding strategy" },
  { icon: Users, t: "Development-team capacity" },
];

const Phase = ({ p, i }) => {
  const Icon = p.icon;
  const done = p.key === "completed";
  return (
    <Reveal delay={i * 0.1} data-testid={`city-phase-${p.key}`}>
      <div className="flex items-center gap-3 mb-6">
        <span className={`h-9 w-9 rounded-full grid place-items-center ${done ? "bg-arq text-white" : p.key === "progress" ? "bg-ink text-white" : "border border-dashed border-arq text-arq"}`}>
          <Icon size={16} />
        </span>
        <div className="text-ink font-medium">{p.title}</div>
        <StatusBadge s={p.s} className="ml-auto" />
      </div>
      <ol className="relative ml-[17px] border-l border-line pl-7 space-y-6">
        {p.items.map((t, j) => (
          <li key={t} className="relative">
            <span className={`absolute -left-[33px] top-1.5 h-2.5 w-2.5 rounded-full ${done ? "bg-arq" : p.key === "progress" ? "bg-ink" : "bg-white border border-arq"}`} />
            <div className="font-mono text-[10px] text-steel tracking-[0.18em]">STEP {String(j + 1).padStart(2, "0")}</div>
            <div className="mt-1 text-[15px] text-char leading-snug">{t}</div>
          </li>
        ))}
      </ol>
    </Reveal>
  );
};

export default function CityStatus() {
  return (
    <Section testid="section-city">
      <SectionHeader num="03" eyebrow="Current City Status" title="Strong City engagement. No executed site control — yet." lede="Status reflects direct October 2026 correspondence between Arqitek and City of Murrieta Economic Development." />
      <Card className="p-8 lg:p-10">
        <div className="grid md:grid-cols-3 gap-10 lg:gap-14">
          {PHASES.map((p, i) => <Phase key={p.key} p={p} i={i} />)}
        </div>
      </Card>
      <Reveal delay={0.2}>
        <div data-testid="city-not-executed-alert" className="mt-6 flex gap-4 items-start rounded-[14px] bg-ink-deep text-white px-8 py-6">
          <AlertTriangle size={20} className="text-arq-mid shrink-0 mt-0.5" />
          <p className="text-[15px] leading-relaxed">
            No Exclusive Negotiating Agreement, ground lease, development agreement or construction authorization has been executed yet.
          </p>
        </div>
      </Reveal>
      <Reveal delay={0.25} className="mt-12">
        <Eyebrow>What the City is currently asking from Arqitek</Eyebrow>
        <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {ASKS.map(({ icon: I, t }) => (
            <Card key={t} data-testid={`city-ask-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="p-6 metric-card">
              <I size={20} className="text-arq" />
              <div className="mt-6 text-ink text-[15px]">{t}</div>
            </Card>
          ))}
        </div>
      </Reveal>
    </Section>
  );
}
